/**
 * React Query hook for fetching compiled (citation-rendered) ShowNotes blocks
 */

import { useQuery } from '@tanstack/react-query'
import httpClient, { pluckData } from 'src/utilities/http-client'
import { ShowNotesBlock } from '../types'

/**
 * Fetch all blocks for a show notes from the API, compiled for display
 * (citation tokens resolved, References block appended if applicable)
 */
async function fetchShowNotesBlocksRendered(
  showNotesID: string,
  basePath: string,
): Promise<ShowNotesBlock[]> {
  let response = await httpClient.get(
    `/api/${basePath}/${showNotesID}/blocks/rendered`,
  )

  let data = pluckData(response)

  // Handle the backend's wrapped format: [{ textBlock: {...} }, { imageBlock: {...} }, { pdfBlock: {...} }]
  if (
    Array.isArray(data) &&
    data.length > 0 &&
    (data[0].textBlock || data[0].imageBlock || data[0].pdfBlock)
  ) {
    let unwrapped = data
      .map((item) => {
        if (item.textBlock) {
          return { ...item.textBlock, blockType: 'text' } as ShowNotesBlock
        }
        if (item.imageBlock) {
          let imageBlock = {
            ...item.imageBlock,
            blockType: 'image',
            caption: item.imageBlock.caption || '',
            altText: item.imageBlock.altText || '',
            widthPercent: item.imageBlock.widthPercent || 100,
            imageUrl: item.imageBlock.imageUrl || '',
          } as ShowNotesBlock
          return imageBlock
        }
        if (item.pdfBlock) {
          let pdfBlock = {
            ...item.pdfBlock,
            blockType: 'pdf',
          } as ShowNotesBlock
          return pdfBlock
        }
        return null
      })
      .filter(Boolean) as ShowNotesBlock[]
    return unwrapped
  }

  // Handle flat format (if backend changes later)
  return data as ShowNotesBlock[]
}

/**
 * Hook to fetch compiled show notes blocks with React Query.
 * Use this for read-only display (previews, viewers) — never for the editor,
 * since the compiled References block has no database row behind it.
 */
export function useShowNotesBlocksRendered(
  showNotesID: string,
  basePath: string,
  enabled: boolean = true,
) {
  let result = useQuery({
    queryKey: ['showNotesBlocksRendered', basePath, showNotesID],
    queryFn: () => fetchShowNotesBlocksRendered(showNotesID, basePath),
    enabled: enabled && !!showNotesID,
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
  })

  return result
}
